import { access, appendFile, mkdir, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";

import { servicePaths } from "./paths.js";

async function pathExists(path) {
    try {
        await access(path);
        return true;
    } catch {
        return false;
    }
}

/** Fail fast with a useful message when not on a provisioned host. */
async function requireDeployRoot(parentDir, serviceRoot) {
    if (await pathExists(parentDir)) return;
    throw new Error(
        `Cannot create ${serviceRoot}: parent directory ${parentDir} does not exist.\n` +
            `This is meant to run on the target host (where ${parentDir} exists). ` +
            `For a local dry run use --appsRoot=/tmp/<service>.`,
    );
}

function resolveKillTimeoutMs(pm2) {
    const explicit = Number(pm2?.killTimeout);
    if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
    const stopSec = Number(pm2?.stopAllowance);
    if (Number.isFinite(stopSec) && stopSec > 0) return Math.floor(stopSec * 1000) + 5000;
    return 65_000;
}

/** How often pm2 re-sends SIGINT / logs "failed to kill - retrying in …" (ms). */
const DEFAULT_KILL_RETRY_TIME_MS = 10_000;

function resolveKillRetryTimeMs(pm2) {
    const explicit = Number(pm2?.killRetryTime);
    if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
    return DEFAULT_KILL_RETRY_TIME_MS;
}

/** Append `--stopAllowance=<sec>` to pm2 args when manifest sets pm2.stopAllowance. */
function resolvePm2Args(pm2) {
    const base = String(pm2?.args ?? "").trim();
    const stopSec = Number(pm2?.stopAllowance);
    if (!Number.isFinite(stopSec) || stopSec <= 0) return base;
    if (/(?:^|\s)--stopAllowance=/.test(base)) return base;
    return `${base}${base ? " " : ""}--stopAllowance=${Math.floor(stopSec)}`.trim();
}

/** CJS snippet required before the app — always force production on deployed hosts. */
function buildEnsureEnvScript() {
    return `/**
 * Written by cli-toolkit deploy (init-structure). Do not hand-edit.
 * Preloaded via pm2 node_args --require. Always set (do not only fill when unset):
 * \`pm2 start … --update-env\` can inject a non-production ENV from the
 * deploy shell / prior dump, and a conditional assign would leave it in place.
 */
process.env.ENV = "production";
process.env.NODE_ENV = "production";
`;
}

function buildEcosystemConfig(service, paths) {
    const { pm2 } = service;
    const outLog = join(paths.logs, `${pm2.appName}.out.log`);
    const errLog = join(paths.logs, `${pm2.appName}.err.log`);
    const killTimeoutMs = resolveKillTimeoutMs(pm2);
    const killRetryTimeMs = resolveKillRetryTimeMs(pm2);
    const args = resolvePm2Args(pm2);
    const ensureEnv = paths.ensureEnv;

    return `/**
 * pm2 ecosystem for ${service.name} — written by cli-toolkit deploy from the
 * service manifest (init/deploy). Do not hand-edit; change pm2.* in the
 * manifest and redeploy. cwd → \`current\` symlink.
 */
module.exports = {
    apps: [
        {
            name: "${pm2.appName}",
            script: "${pm2.script}",
            cwd: "${paths.current}",
            args: ${JSON.stringify(args)},
            // Absolute --require so ENV is set before Args constructs (see shared/ensure-env.cjs).
            node_args: ${JSON.stringify(`--require ${ensureEnv}`)},
            instances: 1,
            exec_mode: "fork",
            autorestart: true,
            min_uptime: "10s",
            max_restarts: 10,
            restart_delay: 2000,
            max_memory_restart: "1500M",
            // Grace window after SIGINT/SIGTERM before SIGKILL (ms). Align with --stopAllowance.
            kill_timeout: ${killTimeoutMs},
            // How often pm2 re-checks / logs "failed to kill - retrying in …"
            // while waiting for graceful exit (pm2 default 100ms → noisy logs).
            kill_retry_time: ${killRetryTimeMs},
            out_file: "${outLog}",
            error_file: "${errLog}",
            merge_logs: true,
            time: true,
            env: {
                // cli-toolkit Args: ENV selects env-suffixed defaults (DB_NAME_PRODUCTION, BUCKET_PRODUCTION, …)
                ENV: "production",
                NODE_ENV: "production",
            },
        },
    ],
};
`;
}

/** Create <appsRoot>/{releases,shared,logs} and sync shared/ecosystem.config.cjs from the manifest. */
export async function initServiceStructure(service, options = {}) {
    const { dryRun = false, logger = console } = options;
    const paths = servicePaths(service);
    const dirs = [paths.releases, paths.shared, paths.logs];
    const created = [];
    const skipped = [];

    if (!dryRun) {
        await requireDeployRoot(dirname(paths.root), paths.root);
    }

    for (const dir of dirs) {
        if (await pathExists(dir)) {
            skipped.push(dir);
            continue;
        }
        if (!dryRun) await mkdir(dir, { recursive: true });
        created.push(dir);
    }

    const ecosystemExisted = await pathExists(paths.ecosystem);
    const ensureEnvExisted = await pathExists(paths.ensureEnv);
    if (!dryRun) {
        await writeFile(paths.ensureEnv, buildEnsureEnvScript(), { mode: 0o644 });
        await writeFile(paths.ecosystem, buildEcosystemConfig(service, paths), { mode: 0o644 });
    }
    if (ensureEnvExisted) {
        logger.info(`synced ${paths.ensureEnv}`);
    } else {
        created.push(paths.ensureEnv);
        logger.info(`seeded ${paths.ensureEnv}`);
    }
    if (ecosystemExisted) {
        logger.info(`synced ${paths.ecosystem} from manifest`);
    } else {
        created.push(paths.ecosystem);
        logger.info(`seeded ${paths.ecosystem}`);
    }

    const line = `[${new Date().toISOString()}] init-structure service=${service.name} dryRun=${dryRun} created=${created.length} skipped=${skipped.length} ecosystem=synced\n`;
    if (!dryRun) {
        await mkdir(paths.logs, { recursive: true });
        await appendFile(paths.deployLog, line);
    }

    logger.info(`service=${service.name} appsRoot=${paths.root}`);
    logger.info(`created: ${created.length ? created.join(", ") : "(none)"}`);
    logger.info(`already present: ${skipped.length ? skipped.join(", ") : "(none)"}`);

    return { paths, created, skipped, ecosystemCreated: !ecosystemExisted, ecosystemSynced: true };
}
